/**
 * V3.2: Chart utilities for pivot report visualization
 */

import type { PivotReportResult } from '../../types'
import { parsePivotCellKey } from '../../types'

// Color palette for chart series
export const PIVOT_COLORS = [
	'#3B82F6', // blue-500
	'#10B981', // emerald-500
	'#F59E0B', // amber-500
	'#EF4444', // red-500
	'#8B5CF6', // violet-500
	'#EC4899', // pink-500
	'#06B6D4', // cyan-500
	'#84CC16', // lime-500
	'#F97316', // orange-500
	'#6366F1', // indigo-500
	'#14B8A6', // teal-500
	'#A855F7', // purple-500
]

/**
 * Transform pivot data to a format compatible with Recharts
 * Each row becomes a data point, with the row values as labels
 */
export interface ChartDataPoint {
	name: string // Row label (combined row values)
	[key: string]: string | number | null // Dynamic column values
}

export function flattenPivotForChart(pivot: PivotReportResult): ChartDataPoint[] {
	return pivot.rows
		.filter((row) => row.rowType === 'detail') // Exclude subtotals
		.map((row) => {
			// Create row label from row values
			const rowLabel = Object.values(row.rowValues)
				.filter((v) => v !== null)
				.join(' / ')

			const dataPoint: ChartDataPoint = {
				name: rowLabel || 'Unknown',
			}

			// Add all cell values
			for (const [key, value] of Object.entries(row.cells)) {
				dataPoint[key] = value
			}

			return dataPoint
		})
}

/**
 * Get unique column keys for chart series
 * Handles both simple and composite keys (with aggregation suffix)
 */
export function getChartSeriesKeys(pivot: PivotReportResult): string[] {
	const keys: string[] = []

	if (pivot.valueConfigs.length > 1) {
		// Multiple aggregations: create composite keys
		for (const colVal of pivot.columnValues) {
			for (const vc of pivot.valueConfigs) {
				keys.push(`${colVal}__${vc.aggregation}`)
			}
		}
	} else {
		// Single aggregation: use column values directly
		keys.push(...pivot.columnValues)
	}

	return keys
}

/**
 * Get display label for a chart series key
 */
export function getSeriesLabel(key: string, pivot: PivotReportResult): string {
	if (pivot.valueConfigs.length > 1) {
		const parsed = parsePivotCellKey(key)
		if (parsed.aggregation) {
			return `${parsed.columnValue} (${parsed.aggregation})`
		}
	}
	return key
}

/**
 * Get color for a series by index
 */
export function getSeriesColor(index: number): string {
	return PIVOT_COLORS[index % PIVOT_COLORS.length]
}

/**
 * Format number for chart tooltip/label
 */
export function formatChartValue(value: number | null, isCount: boolean = false): string {
	if (value === null) return '-'
	if (isCount) return value.toLocaleString()
	return value.toFixed(2)
}
